import {
	Button,
	DataTable,
	PaginatedDataTable,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	useDebounce,
	useSidebar,
} from "@dune/ui";
import { SearchSm } from "@untitled-ui/icons-react";
import { Building2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { PAGES } from "@dune/types";
import { useGetConstructionSites } from "../../../api/queries/constructionSite.queries";
import { useGetGFAConstructionSites } from "../../../api/queries/gfa.queries";
import { useMe } from "../../../api/queries/users.queries";
import PageLayoutWithTabs from "../../../component/layout/PageLayoutWithTabs";
import { usePagination } from "../../../hooks/usePagination";
import { isGFAConstructionSite } from "../../../utils/gfa/gfa.utils";
import {
	constructionSitesColumns,
	constructionSiteSyndicColumns,
	duneConstructionSiteColumns,
	gfaColumns,
} from "./constructionSitesColumns";
import { RoleEnum } from "../../../../../../packages/vars/src";

const ConstructionSitesList: React.FC = () => {
	const navigate = useNavigate();
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 1000);
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";
	const { me: user } = useMe();
	const isSyndic = user?.role === RoleEnum.SYNDIC;
	const isDune = user?.role === RoleEnum.DUNE;
	const hasGfaAccessibility =
		user?.society?.brokerDetails?.accessibleProduct.find(
			(product) => product.authorized && product.gfaProduct,
		) ||
		user?.society?.insurerSociety?.some((is) =>
			is.binders.some((binder) => binder.binderType === "GFA"),
		) ||
		user?.role === RoleEnum.DUNE;
	const [activeTab, setActiveTab] = useState<"constructionSite" | "gfa">(
		"constructionSite",
	);
	const { page, limit, handlePageChange, handleLimitChange } = usePagination();

	const handleTabChange = (value: string) => {
		if (isSyndic && value === "gfa") {
			return;
		}

		if (value === "constructionSite" || value === "gfa") {
			setActiveTab(value);
		}
	};
	const {
		data: constructionSitesResponse = {
			data: [],
			meta: {
				page: 1,
				limit: 10,
				total: 0,
				totalPages: 0,
				hasNextPage: false,
				hasPreviousPage: false,
			},
		},
		isLoading,
		error,
	} = useGetConstructionSites(debouncedSearch, page, limit);

	const constructionSites = constructionSitesResponse.data;
	const paginationMeta = constructionSitesResponse.meta;

	const {
		data: gfaConstructionSites = [],
		isLoading: isLoadingGfas,
		error: errorGfas,
	} = useGetGFAConstructionSites(debouncedSearch);

	if (
		(activeTab === "constructionSite" && error) ||
		(activeTab === "gfa" && errorGfas)
	) {
		return (
			<PageLayoutWithTabs title="Projets" tabContent={{}}>
				<div className="flex flex-col items-center justify-center h-96">
					<Building2 className="h-16 w-16 text-gray-400 mb-4" />
					<h3 className="text-lg font-medium text-gray-900 mb-2">
						Erreur lors du chargement
					</h3>
					<p className="text-gray-500">
						Impossible de charger les chantiers. Veuillez réessayer.
					</p>
				</div>
			</PageLayoutWithTabs>
		);
	}

	return (
		<PageLayoutWithTabs title="Projets" tabContent={{}}>
			<div className="space-y-6 flex-1 min-h-0 flex flex-col">
				{/* Search and Header Actions aligned */}
				<div className="flex flex-row justify-between items-center">
					<div className="flex flex-row gap-2 w-1/2">
						<Input
							type="text"
							placeholder="Recherche rapide"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setSearchInput(e.target.value)
							}
							iconOrText={<SearchSm />}
							iconOrTextPosition="right"
						/>
					</div>
					{!isSyndic && hasGfaAccessibility ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button>Nouveau projet</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent className="w-56" align="start">
								<DropdownMenuItem
									onClick={() => navigate(PAGES.PROJECTS.create)}
								>
									Chantier de construction
								</DropdownMenuItem>
								<DropdownMenuItem onClick={() => navigate(PAGES.GFA.create)}>
									GFA
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<Button onClick={() => navigate(PAGES.PROJECTS.create)}>
							Nouveau projet
						</Button>
					)}
				</div>

				<Tabs
					value={activeTab}
					onValueChange={handleTabChange}
					className="w-full"
				>
					{!isSyndic && hasGfaAccessibility && (
						<TabsList>
							<TabsTrigger value="constructionSite" variant="compact">
								Chantier de construction
							</TabsTrigger>

							<TabsTrigger value="gfa" variant="compact">
								GFA
							</TabsTrigger>
						</TabsList>
					)}
					<TabsContent
						value="constructionSite"
						className="mt-4 flex-1 min-h-0 flex flex-col"
					>
						<div className="flex-1 min-h-0 w-full overflow-auto">
							<div
								className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}
							>
								<PaginatedDataTable
									columns={
										isDune
											? duneConstructionSiteColumns
											: isSyndic
												? constructionSiteSyndicColumns
												: constructionSitesColumns
									}
									data={constructionSites}
									meta={paginationMeta}
									onPageChange={handlePageChange}
									onLimitChange={handleLimitChange}
									isLoading={isLoading}
									noResultsText="Aucun chantier trouvé"
									icon={Building2}
									variant="default-with-sort"
									onRowClick={(constructionSite) => {
										if (isGFAConstructionSite(constructionSite)) {
											navigate(
												PAGES.GFA.show.withParams(
													constructionSite.id.toString(),
												),
											);
										} else {
											navigate(
												PAGES.PROJECTS.show.withParams(
													constructionSite.id.toString(),
												),
											);
										}
									}}
								/>
							</div>
						</div>
					</TabsContent>
					{!isSyndic && hasGfaAccessibility && (
						<TabsContent
							value="gfa"
							className="mt-4 flex-1 min-h-0 flex flex-col"
						>
							<div className="flex-1 min-h-0 w-full overflow-auto">
								<div
									className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}
								>
									<DataTable
										columns={gfaColumns}
										data={gfaConstructionSites}
										isLoading={isLoadingGfas}
										noResultsText="Aucun chantier trouvé"
										icon={Building2}
										variant="default-with-sort"
										onRowClick={(constructionSite) => {
											if (isGFAConstructionSite(constructionSite)) {
												navigate(
													PAGES.GFA.show.withParams(
														constructionSite.id.toString(),
													),
												);
											} else {
												navigate(
													PAGES.PROJECTS.show.withParams(
														constructionSite.id.toString(),
													),
												);
											}
										}}
									/>
								</div>
							</div>
						</TabsContent>
					)}
				</Tabs>
			</div>
		</PageLayoutWithTabs>
	);
};

export default ConstructionSitesList;
